import { useQueryClient } from '@tanstack/react-query';

import type { Product, Variant } from '@nucleus-commerce/api-client/admin';

import { productKeys } from './product-keys';
import { useProductMedia, useSetProductMedia } from './use-product-media';

/**
 * A variant's image is one of the product's own: an image can show several variants, and saving
 * rewrites the gallery's variant links. It acts at once.
 */
export function useVariantImage(product: Product, variant: Pick<Variant, 'id'>) {
  const queryClient = useQueryClient();
  const media = useProductMedia(product.id);
  const save = useSetProductMedia(product.id);
  const items = media.data ?? [];
  const current = items.find((item) => item.variantIds.includes(variant.id))?.mediaId ?? null;

  const pick = (mediaId: string | null, onDone?: () => void) =>
    save.mutate(
      {
        items: items.map((item) => ({
          mediaId: item.mediaId,
          variantIds: [
            ...item.variantIds.filter((id) => id !== variant.id),
            ...(item.mediaId === mediaId ? [variant.id] : []),
          ],
        })),
        featuredMediaId: product.featuredMediaId,
      },
      {
        onSuccess: () => {
          // A variant's `mediaId` is read from these links.
          void queryClient
            .invalidateQueries({ queryKey: productKeys.variants(product.id) })
            .then(() => onDone?.());
        },
      },
    );

  return { items, current, pick, isLoading: media.isPending, save };
}
